import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetAhuCmsWorkflowTasksQuery,
  useGetAhuTaskStatusesQuery,
  useGetAhuProjectTimelineQuery,
} from 'app/services/ahuBoardApi';

/**
 * span#project_status — ViewProjectStatus (§10.2, view/project.status.js +
 * project.status.tmpl). Fetches the CMS workflow tasks (cms 1), master task
 * statuses and the project timeline, then renders
 * "Project Status : <furthest started task> : <status>" — the timeline row
 * with the max task_id having status_id > 1; default task 1 / "not started".
 *
 * [NEG]#40 — the legacy `listenTo(self.project …)` binds to an undefined
 * `self.project` (silent no-op), so status does NOT live-update on project
 * change. Replicated: no live binding.
 *
 * The hidden #checkbox_status_changed is kept as inert markup (CSS/DOM
 * contract). Its legacy `change → render` hook re-read the AHU copy's own
 * TimelineCollection, which the Workflow Manager popup never writes to (it
 * owns a separate instance), so the chip redrew the SAME stale text and the
 * new status appeared only after a reload — unlike the project board, whose
 * copy of the view re-fetches inside render(). Deliberate dedrift: the popup
 * invalidates `AhuProjectTimeline` once its saves land, so the query below
 * re-fetches and the chip updates in place.
 */
const ProjectStatus = ({ projectId }: { projectId: number | undefined }) => {
  const { data: tasks } = useGetAhuCmsWorkflowTasksQuery();
  const { data: statuses } = useGetAhuTaskStatusesQuery();
  const { data: timeline } = useGetAhuProjectTimelineQuery(projectId ?? skipToken);

  let content: string | null = null;
  if (tasks && statuses && timeline) {
    const taskNames: Record<number, string> = {};
    tasks.forEach((t) => {
      taskNames[t.id] = t.name as string;
    });
    const statusNames: Record<number, string> = {};
    statuses.forEach((s) => {
      statusNames[s.id] = s.name;
    });

    let minTaskId = 0;
    let task = taskNames[1];
    let status = 'not started';
    timeline.forEach((row) => {
      const statusId = row.status_id ?? 0;
      if (statusId > 1 && row.task_id > minTaskId && taskNames[row.task_id]) {
        minTaskId = row.task_id;
        task = taskNames[row.task_id];
        status = statusNames[statusId];
      }
    });
    content = `Project Status : ${task} : ${status}`;
  }

  return (
    <span id="project_status" className="project-status conected-to-error-log">
      {content !== null && (
        <>
          <span className="status">{content}</span>
          <input type="checkbox" id="checkbox_status_changed" className="hidden" readOnly />
        </>
      )}
    </span>
  );
};

export default ProjectStatus;
